import { HTMLProps, useRef, useState } from "react"
import cc from "classcat"
import { FileListItem, FileListItemProps } from "./FileListItem"

export interface FileUploadFieldProps extends HTMLProps<HTMLDivElement> {
  id: string
  label: string
  help?: string
  dropAreaLabel?: string
  buttonLabel?: string
  multiple?: boolean
  accept?: string
  fileItems: FileListItemProps[]
  onChangeFiles: (files: FileListItemProps[] | null) => void
}

export const FileUploadField = ({
  id,
  label,
  help,
  dropAreaLabel = "Drop file here",
  buttonLabel = "Choose file",
  multiple = false,
  accept = undefined,
  fileItems = [],
  onChangeFiles,
}: FileUploadFieldProps) => {
  const [dragging, setDragging] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const handleClick = () => {
    fileInput.current?.click()
  }

  const preventDefaults = (e: any) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const handleDragOver = (e: any) => preventDefaults(e)

  const handleDragEnter = (e: any) => {
    preventDefaults(e)
    setDragging(true)
  }

  const handleDragLeave = (e: any) => {
    preventDefaults(e)
    setDragging(false)
  }

  const handleDrop = (e: any) => {
    preventDefaults(e)
    setDragging(false)
    if (multiple) {
      handleFiles([...e.dataTransfer.files])
    } else {
      handleFiles([[...e.dataTransfer.files][0]])
    }
  }

  const handleChange = (e: any) => {
    preventDefaults(e)
    handleFiles([...e.currentTarget.files])
  }

  const handleFiles = (files: HTMLProps<File>[]) => {
    const newFiles = files.map((file) => ({ file }))
    if (multiple) {
      onChangeFiles([...fileItems, ...newFiles])
    } else {
      onChangeFiles(newFiles)
    }
  }

  const handleDeleteFile = (fileName: string | undefined) => {
    if (fileItems) {
      onChangeFiles(
        fileItems.filter(
          (item: FileListItemProps) => item.file.name !== fileName
        )
      )
    }
  }

  return (
    <div className="field field--files">
      <label className="field__label" htmlFor={id}>
        {label}
        {help && <span className="field__help">{help}</span>}

        <span
          className={cc(["drop-area", dragging && "drop-area--dragging"])}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <span className="drop-area--label">{dropAreaLabel}</span>
          <button
            className="drop-area--button btn btn--small"
            onClick={handleClick}
          >
            {buttonLabel}
          </button>
        </span>
      </label>

      <input
        type="file"
        id={id}
        multiple={multiple}
        accept={accept}
        onChange={handleChange}
        ref={fileInput}
      />

      {fileItems.length > 0 && (
        <ul className="block-list file-list">
          {fileItems.map((item: FileListItemProps, i: number) => {
            return (
              <FileListItem {...item} key={i} onDelete={handleDeleteFile} />
            )
          })}
        </ul>
      )}
    </div>
  )
}
